<template>
  <Dialog
    :closable="false"
    :header="headerText"
    :modal="true"
    :visible="isOpen"
    :baseZIndex="base_z_index"
    :draggable="false"
    @hide="onHide"
  >
    <p
      v-if="messageText"
      v-html="messageText"
    />

    <div
      v-if="isDestructive && objectName"
      class="mt-3"
    >
      <p class="mb-2 text-sm">
        {{ t('modal.labels.typeToConfirm', { confirmationInput: objectName }) }}
      </p>

      <InputText
        ref="inputRef"
        v-model="confirmationInput"
        class="p-inputtext w-full"
        data-test="modal-confirm-type-input"
        :placeholder="t('modal.labels.typeHere')"
        @keydown.enter="onAccept"
      />
    </div>

    <template #footer>
      <Button
        class="p-button-text"
        data-test="modal-cancel-primary"
        :label="t('modal.actions.cancel')"
        @click="onCancel"
      />
      <Button
        :class="isDestructive ? 'p-button-danger' : 'p-button-primary'"
        data-test="modal-confirm-primary"
        :disabled="isConfirmDisabled"
        :label="acceptLabel"
        @click="onAccept"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import useGlobalConfirm from '@/composables/useGlobalConfirm';

const { t } = useI18n();
const {
  isOpen,
  isDestructive,
  objectName,
  base_z_index,
  customMessage,
  customHeader,
  customAcceptLabel,
  handleConfirm,
  handleCancel,
} = useGlobalConfirm();

const confirmationInput = ref('');
const inputRef = ref(null);

const headerText = computed(
  () => customHeader.value || (isDestructive.value ? t('modal.labels.dangerZone') : t('modal.labels.areYouSure')),
);

const messageText = computed(() => {
  if (customMessage.value) return customMessage.value;
  return isDestructive.value
    ? t('modal.labels.areYouSureDelete', { object: objectName.value || t('modal.labels.this') })
    : '';
});

const acceptLabel = computed(() => {
  if (customAcceptLabel.value) return customAcceptLabel.value;
  return isDestructive.value ? t('modal.actions.remove') : t('modal.actions.submit');
});

const isConfirmDisabled = computed(() => {
  if (!isDestructive.value || !objectName.value) return false;
  return confirmationInput.value.trim().toLowerCase() !== objectName.value.trim().toLowerCase();
});

// Focus input when dialog opens
watch(isOpen, async (val) => {
  if (val) {
    confirmationInput.value = '';
    await nextTick();
    try {
      if (inputRef.value && typeof inputRef.value.focus === 'function') {
        inputRef.value.focus();
      } else {
        const el = document.querySelector('.p-dialog .p-inputtext');
        if (el && typeof el.focus === 'function') el.focus();
      }
    } catch (e) {
      /* ignore focus failures */
    }
  } else {
    confirmationInput.value = '';
  }
});

const onAccept = () => {
  if (isConfirmDisabled.value) return;
  confirmationInput.value = '';
  handleConfirm();
};

const onCancel = () => {
  confirmationInput.value = '';
  handleCancel();
};

const onHide = () => {
  confirmationInput.value = '';
  handleCancel();
};
</script>
